/**
 * Authentication Store
 *
 * This module provides a Zustand store for managing Streem authentication state.
 * It uses the persist middleware to save authentication state to localStorage,
 * allowing the user to remain logged in between page refreshes.
 *
 * @module store/useAuthStore
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { User, Facility } from '../types/auth';

/**
 * Authentication state interface for Streem authentication system
 *
 * @interface AuthState
 */
interface AuthState {
  // User identity
  user: User | null;
  userId: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;

  // Authentication status
  isAuthenticated: boolean;
  isIdle: boolean;
  loading: boolean;
  error: string | null;

  // Tokens
  accessToken: string | null;
  refreshToken: string | null;
  ssoIdToken: string | null;

  // Permissions
  roles: string[];

  // Facilities
  facilities: Facility[];
  selectedFacility: Facility | null;

  // SSO pending state
  pendingUsername: string | null;

  // Default/allowed user
  isDefaultUser: boolean;
  allowedModules: string[];

  // Actions
  setUser: (user: User) => void;
  setTokens: (accessToken: string, refreshToken: string, ssoIdToken?: string) => void;
  setAccessToken: (accessToken: string) => void;
  setSelectedFacility: (facility: Facility) => void;
  setIdle: (isIdle: boolean) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  login: (user: User, accessToken: string, refreshToken: string, ssoIdToken?: string) => void;
  logout: () => void;
  clearError: () => void;
  setPendingUsername: (username: string | null) => void;
}

const initialState = {
  user: null,
  userId: null,
  firstName: null,
  lastName: null,
  email: null,
  isAuthenticated: false,
  isIdle: false,
  loading: false,
  error: null,
  accessToken: null,
  refreshToken: null,
  ssoIdToken: null,
  roles: [],
  facilities: [],
  selectedFacility: null,
  pendingUsername: null,
  isDefaultUser: false,
  allowedModules: [],
};

/**
 * Authentication store using Zustand with Streem authentication
 *
 * This store manages the authentication state and provides actions for login/logout.
 * It uses the persist middleware to save critical state to localStorage.
 *
 * @example
 * // Using the auth store in a component
 * const { user, isAuthenticated, login, logout } = useAuthStore();
 *
 * // To log a user in
 * login(userData, accessToken, refreshToken);
 *
 * // To log a user out
 * logout();
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      ...initialState,

      // Actions

      /**
       * Set the user object and update related fields
       */
      setUser: (user) => set({
        user,
        userId: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        roles: user.roles,
        facilities: user.facilities,
        selectedFacility: user.facilities[0] || null,
      }),

      /**
       * Set authentication tokens
       */
      setTokens: (accessToken, refreshToken, ssoIdToken) => set({
        accessToken,
        refreshToken,
        ssoIdToken: ssoIdToken || null,
      }),

      /**
       * Update only the access token (for token refresh)
       */
      setAccessToken: (accessToken) => set({ accessToken }),

      /**
       * Set the currently selected facility
       */
      setSelectedFacility: (facility) => set({ selectedFacility: facility }),

      /**
       * Set idle state (for session timeout)
       */
      setIdle: (isIdle) => set({ isIdle }),

      /**
       * Set loading state
       */
      setLoading: (loading) => set({ loading }),

      /**
       * Set error message
       */
      setError: (error) => set({ error }),

      /**
       * Clear error message
       */
      clearError: () => set({ error: null }),

      /**
       * Log a user in with complete authentication data
       */
      login: (user, accessToken, refreshToken, ssoIdToken) => set({
        user,
        userId: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        isAuthenticated: true,
        accessToken,
        refreshToken,
        ssoIdToken: ssoIdToken || null,
        roles: user.roles,
        facilities: user.facilities,
        selectedFacility: user.facilities[0] || null,
        loading: false,
        error: null,
        isDefaultUser: user.isDefaultUser ?? false,
        allowedModules: user.allowedModules ?? [],
      }),

      /**
       * Log a user out and reset state to initial values
       */
      logout: () => set(initialState),

      /**
       * Set pending username for SSO flow
       */
      setPendingUsername: (username) => set({ pendingUsername: username }),
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      // Only persist critical auth data, not loading/error states
      partialize: (state) => ({
        user: state.user,
        userId: state.userId,
        firstName: state.firstName,
        lastName: state.lastName,
        email: state.email,
        isAuthenticated: state.isAuthenticated,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        ssoIdToken: state.ssoIdToken,
        roles: state.roles,
        facilities: state.facilities,
        selectedFacility: state.selectedFacility,
        pendingUsername: state.pendingUsername,
        isDefaultUser: state.isDefaultUser,
        allowedModules: state.allowedModules,
      }),
    }
  )
);
